import React, { useState } from 'react';
import { Box, IconButton, InputAdornment, TextField, Tooltip, Typography } from '@mui/material';
import { Eye, EyeOff } from 'lucide-react';
import logoOnSky from '../../../assets/brand/sbg-logo-full-color-white.svg';
import { sbg, typeStyle } from '../../../theme/sbgTokens';

/*
 * The sign-in family's shared frame (book §12.7, §6.7).
 *
 * Tablet and desktop (sm+): the Sky fills the page, the color-white lockup sits
 * top-left (48px from the edges on desktop) with a quiet "Attendance" product
 * label after a hairline, as in the sidebar, and one white 400px card
 * (radius 20, padding 32) holds the form, and nothing else.
 * Phones: the Sky is a short band holding only the lockup, and the form becomes
 * the full screen as a white sheet that rises over the band.
 *
 * The Sky is cropped (`100% 160%`, from the top, §6.7) so its Cloud horizon
 * falls below the card and the band's lower edge stays blue: the card and the
 * sheet always meet a blue ground. The lockup sits in the upper 45%. In the dark
 * theme the frame switches to the Night gradient.
 */

const SKY_CROP = { backgroundSize: '100% 160%', backgroundPosition: 'top', backgroundRepeat: 'no-repeat' } as const;
const PHONE_BAND_HEIGHT = 168;
const PHONE_SHEET_OVERLAP = 20;

/** Full-page frame for sign-in and password reset. `children` is the form. */
const AuthLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      position: 'relative',
      isolation: 'isolate',
      minHeight: '100vh',
      '@supports (min-height: 100dvh)': { minHeight: '100dvh' },
      display: 'flex',
      flexDirection: 'column',
      bgcolor: sbg.bg.surface,
    }}
  >
    {/* The Sky: a band on phones, the whole page from sm up. Decorative. */}
    <Box
      aria-hidden="true"
      sx={{
        position: 'absolute',
        zIndex: -1,
        top: 0,
        left: 0,
        right: 0,
        bottom: { xs: 'auto', sm: 0 },
        height: { xs: PHONE_BAND_HEIGHT, sm: 'auto' },
        background: sbg.gradient.sky,
        ...SKY_CROP,
        '[data-theme="dark"] &': { background: sbg.gradient.night, ...SKY_CROP },
      }}
    />

    <Box
      component="header"
      sx={{
        flex: 'none',
        position: { xs: 'relative', lg: 'absolute' },
        top: { lg: 0 },
        left: { lg: 0 },
        height: { xs: PHONE_BAND_HEIGHT, sm: 'auto' },
        px: { xs: 2, sm: 4, lg: 6 },
        pt: { xs: 3, sm: 4, lg: 6 },
      }}
    >
      {/* The sidebar's lockup, scaled to the Sky: logo, a 1px on-brand hairline, then the product name. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          component="img"
          src={logoOnSky}
          alt="SBG Funding"
          sx={{ display: 'block', flex: 'none', height: { xs: 40, lg: 44 }, width: 'auto' }}
        />
        <Box aria-hidden="true" sx={{ flex: 'none', width: '1px', height: { xs: 24, lg: 28 }, bgcolor: sbg.border.onBrand }} />
        <Typography variant="labelMd" component="span" noWrap sx={{ color: sbg.text.onBrand }}>
          Attendance
        </Typography>
      </Box>
    </Box>

    <Box
      component="main"
      sx={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: { xs: 'flex-start', sm: 'center' },
        mt: { xs: `-${PHONE_SHEET_OVERLAP}px`, sm: 0 },
        px: { xs: 2, sm: 4 },
        pt: { xs: 4, sm: 6, lg: 8 },
        pb: { xs: 6, sm: 6, lg: 8 },
        // Phones: the form is the screen, a sheet whose rounded top rises over the band.
        bgcolor: { xs: sbg.bg.surface, sm: 'transparent' },
        borderTopLeftRadius: { xs: sbg.radius.panel, sm: 0 },
        borderTopRightRadius: { xs: sbg.radius.panel, sm: 0 },
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 400,
          boxSizing: 'border-box',
          // sm+: the one floating card on the Sky (400px including its 32px padding).
          bgcolor: { sm: sbg.bg.surface },
          borderRadius: { sm: sbg.radius.panel },
          boxShadow: { sm: sbg.shadow.lg },
          p: { xs: 0, sm: 4 },
        }}
      >
        {children}
      </Box>
    </Box>
  </Box>
);

export default AuthLayout;

/** Form title block: heading-2 title, one secondary line. */
export const AuthHeading: React.FC<{ title: React.ReactNode; description?: React.ReactNode }> = ({ title, description }) => (
  <Box sx={{ mb: 4 }}>
    <Typography variant="h2" component="h1" sx={{ color: sbg.text.primary }}>
      {title}
    </Typography>
    {description && (
      <Typography variant="body1" sx={{ color: sbg.text.secondary, mt: 0.5 }}>
        {description}
      </Typography>
    )}
  </Box>
);

/**
 * Comfort-size fields for first-touch screens (book §11.4 `lg`): 44px tall
 * with 16px text, which also keeps iOS from zooming on focus.
 */
export const comfortFieldSx = {
  '& .MuiInputBase-root': { minHeight: sbg.control.lg },
  '& .MuiInputBase-input': { ...typeStyle('body-md'), height: 24, paddingTop: '10px', paddingBottom: '10px' },
};

/** A plain, permissive address check for inline validation (not the server's rule). */
export const looksLikeEmail = (value: string) => /^\S+@\S+\.\S+$/.test(value.trim());

/** One body-xs line (13/18), reserved under password fields so hints and errors don't shift the form. */
const HELPER_LINE_HEIGHT = 18;

export interface PasswordFieldProps {
  id: string;
  label: React.ReactNode;
  value: string;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  autoComplete: 'current-password' | 'new-password';
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
  inputRef?: React.Ref<HTMLInputElement>;
  /** Controlled visibility; leave unset to let the field manage it. */
  visible?: boolean;
  onVisibleChange?: (visible: boolean) => void;
}

/**
 * A password field with a show/hide icon button (aria-label + tooltip) and a
 * quiet Caps Lock hint. The hint follows the helper or error text (it matters
 * most after a failed attempt) in a polite live region, on a helper line that
 * is always reserved so the form doesn't jump while typing.
 */
export const PasswordField: React.FC<PasswordFieldProps> = ({
  id,
  label,
  value,
  onChange,
  autoComplete,
  required,
  disabled,
  error,
  helperText,
  inputRef,
  visible: visibleProp,
  onVisibleChange,
}) => {
  const [visibleState, setVisibleState] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const visible = visibleProp ?? visibleState;
  const toggleLabel = visible ? 'Hide password' : 'Show password';

  const toggle = () => {
    if (onVisibleChange) onVisibleChange(!visible);
    else setVisibleState(!visible);
  };

  const trackCapsLock = (event: React.KeyboardEvent<HTMLInputElement>) => {
    setCapsLock(event.getModifierState('CapsLock'));
  };

  const capsLockOn = capsLock && !disabled;

  return (
    <TextField
      id={id}
      label={label}
      size="medium"
      fullWidth
      type={visible ? 'text' : 'password'}
      value={value}
      onChange={onChange}
      autoComplete={autoComplete}
      disabled={disabled}
      error={error}
      helperText={
        <>
          {helperText}
          <Box component="span" role="status">
            {capsLockOn ? `${helperText ? ' ' : ''}Caps Lock is on.` : ''}
          </Box>
        </>
      }
      inputRef={inputRef}
      sx={{ ...comfortFieldSx, '& .MuiFormHelperText-root': { minHeight: HELPER_LINE_HEIGHT } }}
      slotProps={{
        htmlInput: {
          required,
          onKeyDown: trackCapsLock,
          onKeyUp: trackCapsLock,
          onBlur: () => setCapsLock(false),
        },
        input: {
          endAdornment: (
            <InputAdornment position="end">
              {/* An empty title keeps MUI from wrapping a disabled button's events. */}
              <Tooltip title={disabled ? '' : toggleLabel}>
                <IconButton
                  size="small"
                  aria-label={toggleLabel}
                  aria-controls={id}
                  disabled={disabled}
                  onClick={toggle}
                  onMouseDown={(event) => event.preventDefault()}
                >
                  {visible ? <EyeOff size={18} /> : <Eye size={18} />}
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ),
        },
      }}
    />
  );
};
